<!-- Pantalla pública tipo PDS — sin login, para TV en planta -->
<template>
  <div ref="wrapperRef" class="pds-wrapper">
    <!-- ── Header ──────────────────────────────────────────── -->
    <header class="pds-header">
      <div class="pds-header-left">
        <img src="@/assets/img/Divisan/logo_divisan.png" alt="Divisan" class="pds-logo" />
        <div class="pds-titulo">
          <span class="pds-titulo-main">Producción</span>
          <span class="pds-titulo-sub">Pedidos activos</span>
        </div>
      </div>
      <div class="pds-header-right">
        <span class="pds-contador">
          {{ pedidos.length
          }}<span class="pds-contador-label">
            pedido{{ pedidos.length !== 1 ? "s" : "" }}</span
          >
        </span>
        <div :class="['pds-conexion', conectado ? 'conectado' : 'desconectado']">
          <span class="pds-dot"></span>
          <span class="pds-conexion-label">{{
            conectado ? "En vivo" : "Reconectando..."
          }}</span>
        </div>
        <button
          type="button"
          class="pds-icon-btn"
          :title="enPantallaCompleta ? 'Salir de pantalla completa' : 'Pantalla completa'"
          @click="alternarPantallaCompleta"
        >
          <i
            class="bi"
            :class="enPantallaCompleta ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen'"
          ></i>
        </button>
        <span class="pds-hora">{{ horaActual }}</span>
      </div>
    </header>

    <div v-if="!audioActivado" class="pds-audio-overlay" @click="activarAudio">
      <div class="pds-audio-msg">
        <i class="bi bi-volume-up-fill"></i>
        <span>Toca para activar sonido</span>
      </div>
    </div>

    <!-- ── Contenido ───────────────────────────────────────── -->
    <main class="pds-main">
      <!-- Sin pedidos -->
      <div v-if="pedidos.length === 0" class="pds-vacio">
        <i class="bi bi-inbox pds-vacio-icon"></i>
        <p>No hay pedidos activos</p>
      </div>

      <!-- Grid de tarjetas -->
      <TransitionGroup v-else name="pds-card" tag="div" class="pds-grid">
        <PedidoCardPDS v-for="p in pedidosOrdenados" :key="p.numero_pedido" :pedido="p" />
      </TransitionGroup>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import PedidoCardPDS from "../TiempoReal/PedidosCardPds.vue";
import { usePedidosPDS } from "../TiempoReal/usePedidosPds.js";
import { ordenarPedidos } from "../TiempoReal/ordenarPedidos.js";

const { pedidos, conectado, error, conectar } = usePedidosPDS();
const audioActivado = ref(false);

function activarAudio() {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  ctx.resume().then(() => {
    audioActivado.value = true;
    ctx.close();
  });
}

// Ordenamiento canónico: prioridad → fecha compromiso → total piezas
const pedidosOrdenados = computed(() => ordenarPedidos(pedidos.value));

// Reloj en tiempo real
const horaActual = ref("");
function actualizarHora() {
  horaActual.value = new Date().toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
setInterval(actualizarHora, 1000);
actualizarHora();

onMounted(conectar);

// ── Pantalla completa ──────────────────────────────────────
const wrapperRef = ref(null);
const enPantallaCompleta = ref(false);

function actualizarFullscreen() {
  enPantallaCompleta.value = Boolean(document.fullscreenElement);
}

async function alternarPantallaCompleta() {
  if (document.fullscreenElement) {
    await document.exitFullscreen();
  } else {
    await wrapperRef.value?.requestFullscreen?.();
  }
}

onMounted(() => document.addEventListener("fullscreenchange", actualizarFullscreen));
onBeforeUnmount(() => document.removeEventListener("fullscreenchange", actualizarFullscreen));
</script>

<style scoped>
/* ── Wrapper ─────────────────────────────────────────────── */
.pds-wrapper {
  min-height: 100vh;
  font-family: system-ui, sans-serif;
  background: linear-gradient(rgba(20, 30, 40, 0.5), rgba(20, 30, 40, 0.6)),
    url("@/assets/img/Divisan/img_2.jpg");
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
}

/* ── Header ─────────────────────────────────────────────── */
.pds-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 1.25rem;
  background: rgba(0, 0, 0, 0.45);
  border-bottom: 1px solid rgba(254, 250, 224, 0.1);
  flex-shrink: 0;
  gap: 0.5rem;
}
.pds-header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}
.pds-logo {
  height: 30px;
  flex-shrink: 0;
  filter: brightness(0) invert(1);
  opacity: 0.9;
}
.pds-titulo {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
}
.pds-titulo-main {
  font-size: 1rem;
  font-weight: 800;
  color: var(--d-cream);
  line-height: 1;
  white-space: nowrap;
}
.pds-titulo-sub {
  font-size: 0.68rem;
  color: rgba(254, 250, 224, 0.5);
  font-weight: 500;
  white-space: nowrap;
}
.pds-header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}
.pds-contador {
  font-size: 0.9rem;
  font-weight: 800;
  color: var(--d-cream);
}
.pds-contador-label {
  font-weight: 400;
  color: rgba(254, 250, 224, 0.6);
  font-size: 0.78rem;
}
.pds-hora {
  font-family: var(--d-font-mono);
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--d-cream);
  letter-spacing: 0.05em;
  white-space: nowrap;
}

/* Indicador de conexión */
.pds-conexion {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.74rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}
.pds-conexion.conectado {
  background: rgba(46, 125, 50, 0.3);
  color: #a5d6a7;
}
.pds-conexion.desconectado {
  background: rgba(192, 57, 43, 0.3);
  color: #ef9a9a;
}
.pds-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.conectado .pds-dot {
  background: #69f0ae;
  box-shadow: 0 0 6px #69f0ae;
  animation: pulso 2s infinite;
}
.desconectado .pds-dot {
  background: #ef9a9a;
}
@keyframes pulso {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

/* Botón de pantalla completa */
.pds-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(254, 250, 224, 0.18);
  background: rgba(254, 250, 224, 0.08);
  color: var(--d-cream);
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}
.pds-icon-btn:hover {
  background: rgba(254, 250, 224, 0.18);
}

/* ── Main ────────────────────────────────────────────────── */
.pds-main {
  flex: 1;
  padding: 0.75rem 0.75rem;
}

/* ── Grid responsivo ─────────────────────────────────────── */
/* Flexbox en vez de CSS Grid: cuando la última fila no se completa
   (p. ej. 8 pedidos en una grilla de 3 columnas), Grid la deja alineada
   a la izquierda con un hueco vacío a la derecha; Flexbox + justify-content
   centrado permite que esa fila incompleta quede centrada en su lugar. */

/* Móvil por defecto: 1 columna */
.pds-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.65rem;
}
.pds-grid > * {
  flex: 1 1 100%;
  max-width: 100%;
}

/* Tablet: 2 columnas */
@media (min-width: 600px) {
  .pds-grid {
    gap: 0.75rem;
  }
  .pds-grid > * {
    flex: 1 1 calc(50% - 0.375rem);
    max-width: calc(50% - 0.375rem);
  }
  .pds-main {
    padding: 0.85rem 1rem;
  }
}

/* Desktop: 3 columnas */
@media (min-width: 1024px) {
  .pds-grid {
    gap: 0.85rem;
  }
  .pds-grid > * {
    flex: 1 1 calc(33.333% - 0.567rem);
    max-width: calc(33.333% - 0.567rem);
  }
  .pds-header {
    padding: 0.7rem 2rem;
  }
  .pds-logo {
    height: 36px;
  }
  .pds-titulo-main {
    font-size: 1.1rem;
  }
  .pds-main {
    padding: 0.85rem 1.25rem;
  }
  .pds-hora {
    font-size: 1rem;
  }
}

/* Header: ocultar label "pedidos" y hora en pantallas muy pequeñas */
@media (max-width: 420px) {
  .pds-contador-label {
    display: none;
  }
  .pds-hora {
    display: none;
  }
  .pds-titulo-sub {
    display: none;
  }
  .pds-conexion-label {
    display: none;
  }
  .pds-conexion {
    padding: 4px 6px;
  }
}

/* ── Vacío ───────────────────────────────────────────────── */
.pds-vacio {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  color: rgba(254, 250, 224, 0.3);
  font-size: 1rem;
  font-weight: 700;
  gap: 0.75rem;
}
.pds-vacio-icon {
  font-size: 3.5rem;
  display: block;
}

/* ── Animaciones TransitionGroup ─────────────────────────── */
.pds-card-enter-active {
  transition: all 0.4s ease;
}
.pds-card-leave-active {
  transition: all 0.3s ease;
}
.pds-card-enter-from {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}
.pds-card-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
.pds-card-move {
  transition: transform 0.3s ease;
}

/* ── Audio overlay ───────────────────────────────────────── */
.pds-audio-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  cursor: pointer;
}
.pds-audio-msg {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  color: var(--d-cream);
  font-size: 1.5rem;
  font-weight: 800;
}
.pds-audio-msg i {
  font-size: 3rem;
}
</style>
